import { ApiPaths } from '@crossbeam/openapi/src/api';
import { Nullable, StrictExtract } from '@crossbeam/types';

import { DateTime } from 'luxon';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { MatchesExportPayload } from '@/types/populations';
import {
  DealNavigatorExportPayload,
  PipelineGenerationExportPayload,
  ReportExportPayload,
  ReportExportPayloadV5,
} from '@/types/reports';

const DATA_LIMIT = 1000000;
const DATA_LIMIT_STR = '1 million';
const DEFAULT_EXPORT_NAME = 'Crossbeam';

type ExportUrl = StrictExtract<
  ApiPaths,
  | '/v0.3/report-data/export'
  | '/v0.5/report-data/export'
  | '/v0.3/matches/export'
  | '/v0.1/records/deal-nav/open-opportunities/export'
  | '/v0.1/records/deal-nav/lead-generation/export'
>;

type ExportPayload =
  | ReportExportPayload
  | ReportExportPayloadV5
  | MatchesExportPayload
  | DealNavigatorExportPayload
  | PipelineGenerationExportPayload;

const getCleansedExportName = (exportName: Nullable<string> = null) => {
  const name = exportName || DEFAULT_EXPORT_NAME;
  return name.replace(/ /g, '_').replace(/[^a-zA-Z0-9_\\-]/gi, '');
};

const validOrUnknownSize = (size: Nullable<number> = null) => {
  return !size || size <= DATA_LIMIT;
};
export default function useExports() {
  const { currentUser } = useAuth();
  const flashesStore = useFlashesStore();
  const getFlashDesc = (size: Nullable<number> = null) => {
    return validOrUnknownSize(size)
      ? `You will receive an email at ${currentUser.value.email} once it completes.`
      : `Your export will include the first ${DATA_LIMIT_STR} results.
          If you need a larger export, please contact us at support@crossbeam.com.`;
  };

  async function performExport(
    exportPayload: ExportPayload,
    exportUrl: ExportUrl,
    exportSize: Nullable<number> = null,
  ) {
    const timestamp = DateTime.local().toFormat('y-MM-dd_hhmma');
    const cleansedExportName = getCleansedExportName(exportPayload.export_name);
    const payload = {
      ...exportPayload,
      requested_filename: `${cleansedExportName}_${timestamp}`,
    };
    const { error } = await crossbeamApi.POST(exportUrl, {
      body: payload,
    });
    if (error) {
      flashesStore.addErrorFlash({
        message: 'Export failed. Try again',
        description: 'If the error persists contact support@crossbeam.com',
      });
      captureException(error);
      return;
    }
    const flashObject = {
      message: 'We have started your export.',
      description: getFlashDesc(exportSize),
    };
    flashesStore.addSuccessFlash(flashObject);
  }

  return { performExport };
}
